import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

/**
 * Banner do modo suporte (REQ-006 / AB#35).
 *
 * Renderiza duas variantes:
 *   - Consultor: quando o usuário autenticado tem um JWT com `tipo=impersonate`
 *     (sub = usuário-alvo; impersonado_por = consultor real). Fixa no topo,
 *     mostra "Modo suporte ativo — [Nome do usuário] · [Tenant] · Chamado #N"
 *     com botão Encerrar sessão. Faz heartbeat a cada 60s (RN-13).
 *   - Usuário: quando o backend informa que há sessão de impersonate ativa para
 *     o usuário autenticado (GET /impersonate/active-for-me). Mostra "Sessão
 *     de suporte ativa · [Nome do consultor] está acessando sua conta" com
 *     botão Encerrar acesso.
 *
 * O banner ocupa o topo da tela (sticky, z-50) e não é ocultável (RN-08).
 * Polling a cada 30s para o lado do usuário detectar início/fim de sessão.
 */

interface SessaoConsultor {
  id: number;
  iniciado_em: string;
  nome_consultor: string;
  nome_alvo: string;
  nome_tenant: string;
  numero_chamado: string;
  titulo_chamado: string;
  encerrada: boolean;
}

interface SessaoUsuario {
  id: number;
  iniciado_em: string;
  nome_consultor: string;
  numero_chamado: string;
  titulo_chamado: string;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const partes = token.split('.');
    if (partes.length !== 3) return null;
    return JSON.parse(atob(partes[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

export function ImpersonateBanner(): JSX.Element | null {
  const accessToken = useAuthStore((s) => s.access_token);
  const usuario = useAuthStore((s) => s.usuario);

  const [comoConsultor, setComoConsultor] = useState<SessaoConsultor | null>(null);
  const [comoUsuario, setComoUsuario] = useState<SessaoUsuario | null>(null);
  const [encerrando, setEncerrando] = useState(false);

  // Detecta se o JWT é de impersonate (sou o consultor navegando).
  const payload = accessToken ? parseJwtPayload(accessToken) : null;
  const ehImpersonate = payload?.tipo === 'impersonate';
  const impersonateId =
    typeof payload?.impersonate_id === 'number' ? payload.impersonate_id : null;

  // Carrega detalhes da sessão para o consultor.
  useEffect(() => {
    if (!ehImpersonate || !impersonateId) {
      setComoConsultor(null);
      return;
    }
    let ativo = true;
    void (async () => {
      try {
        const res = await apiFetch(`/impersonate/${impersonateId}`);
        if (!res.ok) return;
        const data = (await res.json()) as SessaoConsultor;
        if (ativo) setComoConsultor(data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [ehImpersonate, impersonateId]);

  // Polling de sessão ativa para o usuário (banner do usuário).
  const carregarComoUsuario = useCallback(async () => {
    if (!usuario || ehImpersonate) {
      setComoUsuario(null);
      return;
    }
    try {
      const res = await apiFetch('/impersonate/active-for-me');
      if (!res.ok) {
        setComoUsuario(null);
        return;
      }
      const data = (await res.json()) as SessaoUsuario | null;
      setComoUsuario(data);
    } catch {
      setComoUsuario(null);
    }
  }, [usuario, ehImpersonate]);

  useEffect(() => {
    void carregarComoUsuario();
    const iv = window.setInterval(() => void carregarComoUsuario(), 30_000);
    return () => window.clearInterval(iv);
  }, [carregarComoUsuario]);

  // Heartbeat do consultor (RN-13) — a cada 60s.
  useEffect(() => {
    if (!comoConsultor || comoConsultor.encerrada) return;
    const enviar = async (): Promise<void> => {
      try {
        await apiFetch(`/impersonate/${comoConsultor.id}/heartbeat`, { method: 'POST' });
      } catch {
        /* ignore */
      }
    };
    void enviar();
    const iv = window.setInterval(() => void enviar(), 60_000);
    return () => window.clearInterval(iv);
  }, [comoConsultor]);

  async function encerrarComoConsultor(): Promise<void> {
    if (!comoConsultor) return;
    setEncerrando(true);
    try {
      await apiFetch(`/impersonate/${comoConsultor.id}/encerrar`, { method: 'POST' });
    } catch {
      /* ignore */
    } finally {
      // Após encerrar, descarta o JWT de impersonate e volta ao login.
      useAuthStore.getState().clear();
      window.location.href = '/login';
    }
  }

  async function encerrarComoUsuario(): Promise<void> {
    if (!comoUsuario) return;
    setEncerrando(true);
    try {
      await apiFetch(`/impersonate/${comoUsuario.id}/encerrar`, { method: 'POST' });
      setComoUsuario(null);
    } finally {
      setEncerrando(false);
    }
  }

  if (comoConsultor) {
    return (
      <div
        role="status"
        className="sticky top-0 z-50 flex w-full items-center justify-between gap-4 border-b border-blue-700 bg-blue-600 px-6 py-3 text-sm text-white shadow-md"
      >
        <div className="flex items-center gap-3">
          <span className="text-base">🔵</span>
          <div>
            <p className="font-semibold">
              Modo suporte ativo — {comoConsultor.nome_alvo}{' '}
              <span className="font-normal opacity-90">· {comoConsultor.nome_tenant}</span>
            </p>
            <p className="text-xs opacity-90">
              Chamado {comoConsultor.numero_chamado} · Iniciado em{' '}
              {new Date(comoConsultor.iniciado_em).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void encerrarComoConsultor()}
          disabled={encerrando}
          className="rounded-md border border-white/40 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide transition hover:bg-white/20 disabled:opacity-60"
        >
          {encerrando ? 'Encerrando…' : 'Encerrar sessão'}
        </button>
      </div>
    );
  }

  if (comoUsuario) {
    return (
      <div
        role="status"
        className="sticky top-0 z-50 flex w-full items-center justify-between gap-4 border-b border-blue-700 bg-blue-600 px-6 py-3 text-sm text-white shadow-md"
      >
        <div className="flex items-center gap-3">
          <span className="text-base">🔵</span>
          <div>
            <p className="font-semibold">Sessão de suporte ativa</p>
            <p className="text-xs opacity-90">
              {comoUsuario.nome_consultor} está acessando sua conta para o chamado{' '}
              {comoUsuario.numero_chamado}.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void encerrarComoUsuario()}
          disabled={encerrando}
          className="rounded-md border border-white/40 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide transition hover:bg-white/20 disabled:opacity-60"
        >
          {encerrando ? 'Encerrando…' : 'Encerrar acesso'}
        </button>
      </div>
    );
  }

  return null;
}
